import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, ChevronRight, Sparkles, Info, Baby } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import AiAnalysisCard from "@/components/AiAnalysisCard";

const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmt = (d, isNl) => isNaN(d.getTime()) ? "—" : d.toLocaleDateString(isNl ? "nl-NL" : "en-GB", { day: "2-digit", month: "long", year: "numeric" });

const FoalingDateCalculatorPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const today = new Date().toISOString().slice(0, 10);
  const [coverDate, setCoverDate] = useState(today);
  const [gestation, setGestation] = useState(340);

  useEffect(() => {
    document.title = isNl
      ? "Veulendatum Calculator — bereken verwachte veulendatum van je merrie | HorseAppCompare"
      : "Foaling Date Calculator — predict your mare's due date | HorseAppCompare";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", isNl
      ? "Voer de dekkings- of inseminatiedatum in en zie precies wanneer je merrie veulent (340 dagen). Inclusief mijlpalen, vaccinaties en wanneer je dierenarts inschakelt."
      : "Enter the covering or insemination date and see exactly when your mare is due (340 days). Includes milestones, vaccinations and when to call the vet.");
  }, [isNl]);

  const result = useMemo(() => {
    const base = new Date(coverDate);
    if (isNaN(base.getTime())) return null;
    const earliest = addDays(base, 320);
    const expected = addDays(base, gestation);
    const latest = addDays(base, 365);
    const daysLeft = Math.ceil((expected - new Date()) / (1000 * 60 * 60 * 24));
    return { earliest, expected, latest, daysLeft };
  }, [coverDate, gestation]);

  const milestones = useMemo(() => {
    if (!result) return [];
    const base = new Date(coverDate);
    return [
      { day: 16, key: "scan1", nl: "1e echo-scan (drachtigheidsbevestiging)", en: "1st ultrasound (pregnancy confirmation)" },
      { day: 30, key: "scan2", nl: "2e echo (hartslag check)", en: "2nd scan (heartbeat check)" },
      { day: 60, key: "scan3", nl: "Geslachts-echo (optioneel)", en: "Sex scan (optional)" },
      { day: 150, key: "rhino1", nl: "Rhinopneumonie-vaccinatie (5e mnd)", en: "Rhinopneumonitis vaccination (5th month)" },
      { day: 210, key: "rhino2", nl: "Rhinopneumonie booster (7e mnd)", en: "Rhinopneumonitis booster (7th month)" },
      { day: 270, key: "rhino3", nl: "Rhinopneumonie 3e vaccinatie (9e mnd)", en: "Rhinopneumonitis 3rd vaccination (9th month)" },
      { day: 300, key: "tetanus", nl: "Tetanus + influenza booster (10e mnd)", en: "Tetanus + influenza booster (10th month)" },
      { day: 320, key: "ready", nl: "Veulenstal voorbereiden, foaling kit klaar", en: "Prepare foaling box + foaling kit ready" },
      { day: 330, key: "watch", nl: "Dagelijks uier-check + nachtcamera aan", en: "Daily udder check + night camera on" },
      { day: 340, key: "due", nl: "Verwachte veulendatum (±15 dgn normaal)", en: "Expected foaling date (±15 days normal)" },
    ].map(m => ({ ...m, date: fmt(addDays(base, m.day), isNl), passed: addDays(base, m.day) < new Date() }));
  }, [coverDate, result, isNl]);

  return (
    <main data-testid="foaling-page">
      <section className="bg-gradient-to-br from-pink-50 via-white to-white border-b border-[#E2E8F0]">
        <div className="hac-container py-14 max-w-4xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-4"><Link to="/" className="hover:text-[#2563EB]">Home</Link><ChevronRight size={12} /><span className="text-[#0F172A] font-medium">{isNl ? "Veulendatum" : "Foaling date"}</span></nav>
          <div className="overline mb-2 text-pink-600 inline-flex items-center gap-1.5"><Baby size={12} /> {isNl ? "Gratis tool voor fokkers" : "Free tool for breeders"}</div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-3">{isNl ? "Veulendatum Calculator" : "Foaling Date Calculator"}</h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl">{isNl
            ? "Bereken de verwachte veulendatum van je merrie op basis van de dekkings- of inseminatiedatum. Inclusief vaccinatie-mijlpalen en wanneer je dierenarts wilt inschakelen."
            : "Predict your mare's foaling date from the covering or insemination date. Includes vaccination milestones and when to involve your vet."}</p>
        </div>
      </section>

      <section className="hac-container py-10 max-w-6xl grid lg:grid-cols-[400px_1fr] gap-6">
        <form onSubmit={(e) => e.preventDefault()} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-5 h-fit lg:sticky lg:top-4">
          <h2 className="font-bold text-xl text-[#0F172A]">{isNl ? "Invoer" : "Inputs"}</h2>
          <div><label className="overline block mb-1 text-[#64748B]">{isNl ? "Dekkings- of inseminatiedatum" : "Covering or insemination date"} *</label><input type="date" required className="hac-input" value={coverDate} onChange={(e) => setCoverDate(e.target.value)} data-testid="foal-coverdate" /></div>
          <div><label className="overline block mb-1 text-[#64748B]">{isNl ? "Verwachte drachtduur (dagen)" : "Expected gestation (days)"}</label><select className="hac-input" value={gestation} onChange={(e) => setGestation(parseInt(e.target.value))} data-testid="foal-gestation">
            <option value={330}>{isNl ? "330 dgn (kortere lijn)" : "330 days (shorter line)"}</option>
            <option value={340}>{isNl ? "340 dgn (gemiddeld)" : "340 days (average)"}</option>
            <option value={345}>{isNl ? "345 dgn (warmbloed-merries)" : "345 days (warmblood mares)"}</option>
            <option value={350}>{isNl ? "350 dgn (langere lijn)" : "350 days (longer line)"}</option>
          </select><p className="text-[11px] text-[#94A3B8] mt-1">{isNl ? "Normale range: 320-365 dagen." : "Normal range: 320-365 days."}</p></div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs flex gap-2 items-start"><Info size={14} className="text-blue-600 shrink-0 mt-0.5" /><p className="text-blue-900">{isNl ? "Een gemiddelde drachtigheid is 340 dagen. Variatie van 320-365 dagen is volledig normaal." : "Average gestation is 340 days. Variation from 320-365 days is completely normal."}</p></div>
        </form>

        <div className="space-y-4">
          {result && (
            <div className="bg-gradient-to-br from-pink-600 to-rose-700 text-white rounded-2xl p-6">
              <div className="text-xs uppercase tracking-widest text-pink-200 mb-2 inline-flex items-center gap-1.5"><Sparkles size={12} /> {isNl ? "Verwachte veulendatum" : "Expected foaling date"}</div>
              <div className="text-4xl md:text-5xl font-bold mb-2" data-testid="foal-expected">{fmt(result.expected, isNl)}</div>
              <div className="text-pink-200 text-sm">{result.daysLeft > 0 ? (isNl ? `Nog ${result.daysLeft} dagen` : `${result.daysLeft} days to go`) : (isNl ? `${Math.abs(result.daysLeft)} dagen geleden — controleer merrie!` : `${Math.abs(result.daysLeft)} days ago — check mare!`)}</div>
              <div className="mt-4 pt-4 border-t border-white/20 grid grid-cols-2 gap-3 text-xs">
                <div><div className="text-pink-200 uppercase">{isNl ? "Vroegste (320d)" : "Earliest (320d)"}</div><div className="font-medium">{fmt(result.earliest, isNl)}</div></div>
                <div><div className="text-pink-200 uppercase">{isNl ? "Laatste (365d)" : "Latest (365d)"}</div><div className="font-medium">{fmt(result.latest, isNl)}</div></div>
              </div>
            </div>
          )}

          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
            <h3 className="font-bold text-[#0F172A] mb-4 inline-flex items-center gap-2"><Calendar size={18} className="text-pink-600" /> {isNl ? "Mijlpalen tijdens de dracht" : "Pregnancy milestones"}</h3>
            <ol className="space-y-2">
              {milestones.map((m) => (
                <li key={m.key} className={`flex items-start gap-3 p-2.5 rounded-lg ${m.passed ? "bg-slate-50 text-slate-500" : "bg-pink-50"}`} data-testid={`foal-milestone-${m.key}`}>
                  <div className={`shrink-0 w-12 text-center font-mono text-xs font-bold ${m.passed ? "text-slate-400" : "text-pink-700"}`}>D{m.day}</div>
                  <div className="flex-1">
                    <div className={`text-sm ${m.passed ? "line-through" : "text-[#0F172A] font-medium"}`}>{isNl ? m.nl : m.en}</div>
                    <div className="text-[11px] text-[#94A3B8]">{m.date}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs">
            <p className="text-amber-900"><strong>{isNl ? "Disclaimer:" : "Disclaimer:"}</strong> {isNl ? "Deze data zijn indicaties — overleg met je dierenarts voor het vaccinatieschema en bij eerste tekenen van vroege geboorte." : "Dates are indications — consult your vet for the vaccination schedule and at first signs of premature foaling."}</p>
          </div>

          {result && (
            <AiAnalysisCard tool="foaling" accent="pink" inputs={{
              cover_date: coverDate,
              expected_date: result.expected.toISOString().slice(0, 10),
              days_in: Math.max(0, gestation - result.daysLeft),
              gestation: gestation,
            }} />
          )}
        </div>
      </section>
    </main>
  );
};

export default FoalingDateCalculatorPage;
